/**
 * Channel Settings Modal Component
 * Modal for editing channel settings, managing members, and archiving/deleting channels
 */

import { useState } from 'react';
import {
  useUpdateChannel,
  useArchiveChannel,
  useUnarchiveChannel,
  useDeleteChannel,
  useSetDefaultChannel,
} from '@/hooks/useChannels';
import {
  useChannelNotificationLevel,
  useUpdateChannelNotificationLevel,
} from '@/hooks/useChannelNotificationLevel';
import { useAuthStore } from '@/store/auth.store';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import type { Channel } from '@/types/api.types';

interface ChannelSettingsModalProps {
  channel: Channel;
  onClose: () => void;
  onOpenAddMembers?: () => void;
}

export const ChannelSettingsModal = ({
  channel,
  onClose,
  onOpenAddMembers,
}: ChannelSettingsModalProps) => {
  const [name, setName] = useState(channel.name);
  const [description, setDescription] = useState(channel.description || '');
  const [type, setType] = useState<'public' | 'private' | 'announcement'>(channel.type);
  const [position, setPosition] = useState(channel.position ?? 0);
  const [error, setError] = useState<string | null>(null);
  const { dialogRef, titleId } = useDialogA11y(onClose);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const updateChannelMutation = useUpdateChannel();
  const archiveChannelMutation = useArchiveChannel();
  const unarchiveChannelMutation = useUnarchiveChannel();
  const deleteChannelMutation = useDeleteChannel();
  const setDefaultChannelMutation = useSetDefaultChannel();

  // Notification level management
  const { data: notificationLevel } = useChannelNotificationLevel(channel.id);
  const updateNotificationMutation = useUpdateChannelNotificationLevel(channel.id);

  // Update local state if channel prop changes (adjust state during render on change)
  const [syncedChannel, setSyncedChannel] = useState(channel);
  if (channel !== syncedChannel) {
    setSyncedChannel(channel);
    setName(channel.name);
    setDescription(channel.description || '');
    setType(channel.type);
    setPosition(channel.position ?? 0);
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Името на канала е задължително');
      return;
    }

    try {
      await updateChannelMutation.mutateAsync({
        channelId: channel.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          type,
          position,
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Грешка при актуализиране на канал');
    }
  };

  const handleArchive = async () => {
    try {
      if (channel.is_archived) {
        await unarchiveChannelMutation.mutateAsync(channel.id);
      } else {
        await archiveChannelMutation.mutateAsync(channel.id);
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Грешка при ${channel.is_archived ? 'възстановяване' : 'архивиране'} на канал`
      );
    }
  };

  const handleDelete = async () => {
    try {
      await deleteChannelMutation.mutateAsync(channel.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Грешка при изтриване на канал');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50" onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border-primary sticky top-0 bg-white dark:bg-dark-bg-elevated">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Настройки на канал
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary rounded transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Basic Settings Form */}
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
                Основни настройки
              </h3>

              {/* Name Input */}
              <div className="mb-4">
                <label
                  htmlFor="channel-name"
                  className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                >
                  Име на канала *
                </label>
                <input
                  id="channel-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  placeholder="например: general, marketing"
                  maxLength={50}
                  disabled={channel.is_archived}
                />
              </div>

              {/* Description Input */}
              <div className="mb-4">
                <label
                  htmlFor="channel-description"
                  className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                >
                  Описание
                </label>
                <textarea
                  id="channel-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input resize-none"
                  rows={3}
                  placeholder="Кратко описание на канала..."
                  maxLength={200}
                  disabled={channel.is_archived}
                />
              </div>

              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Тип канал *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="channel-type"
                      value="public"
                      checked={type === 'public'}
                      onChange={(e) => setType(e.target.value as 'public')}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      disabled={channel.is_archived}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Публичен</div>
                      <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                        Всеки може да види и присъедини към този канал
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="channel-type"
                      value="private"
                      checked={type === 'private'}
                      onChange={(e) => setType(e.target.value as 'private')}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      disabled={channel.is_archived}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Частен</div>
                      <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                        Само поканени потребители могат да видят този канал
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="channel-type"
                      value="announcement"
                      checked={type === 'announcement'}
                      onChange={(e) => setType(e.target.value as 'announcement')}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      disabled={channel.is_archived}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Обяви</div>
                      <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                        Само администратори могат да публикуват съобщения
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Position Input */}
              <div className="mt-4">
                <label
                  htmlFor="channel-position"
                  className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                >
                  Позиция (ред на показване)
                </label>
                <input
                  id="channel-position"
                  type="number"
                  min={0}
                  value={position}
                  onChange={(e) => setPosition(Math.max(0, parseInt(e.target.value) || 0))}
                  className="input w-32"
                  disabled={channel.is_archived}
                />
                <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                  По-малък номер = по-нагоре в списъка
                </p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            {/* Update Button */}
            {!channel.is_archived && (
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updateChannelMutation.isPending}
                >
                  {updateChannelMutation.isPending ? 'Запазване...' : 'Запази промените'}
                </button>
              </div>
            )}
          </form>

          {/* Members Section */}
          {onOpenAddMembers && (
            <div className="border-t border-gray-200 dark:border-dark-border-primary pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Членове на канала
                </h3>
                {!channel.is_archived && (
                  <button
                    onClick={onOpenAddMembers}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    + Добави членове
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
                {channel.member_count || 0} {channel.member_count === 1 ? 'член' : 'членове'}
              </p>
            </div>
          )}

          {/* Notification Settings */}
          <div className="border-t border-gray-200 dark:border-dark-border-primary pt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
              Настройки за известия
            </h3>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Ниво на известия
              </label>
              <select
                value={notificationLevel || 'all'}
                onChange={(e) => {
                  const level = e.target.value as 'all' | 'mentions' | 'none';
                  updateNotificationMutation.mutate(level);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-secondary dark:bg-dark-bg-input dark:text-dark-text-primary rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                disabled={updateNotificationMutation.isPending}
              >
                <option value="all">Всички съобщения</option>
                <option value="mentions">Само споменавания (@)</option>
                <option value="none">Заглушен канал</option>
              </select>

              {/* Description based on selected level */}
              <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                {(notificationLevel || 'all') === 'all' && (
                  <>Ще получавате известия за всички нови съобщения в този канал</>
                )}
                {notificationLevel === 'mentions' && (
                  <>Ще получавате известия само когато ви споменат (@име) в този канал</>
                )}
                {notificationLevel === 'none' && (
                  <>Няма да получавате никакви известия от този канал</>
                )}
              </p>

              {updateNotificationMutation.isPending && (
                <p className="text-xs text-blue-600">Запазва се...</p>
              )}
            </div>
          </div>

          {/* Default Channel Toggle (admin only) */}
          {isAdmin && !channel.is_archived && (
            <div className="border-t border-gray-200 dark:border-dark-border-primary pt-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
                Канал по подразбиране
              </h3>
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                    {channel.is_default ? 'Това е каналът по подразбиране' : 'Задай като канал по подразбиране'}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-dark-text-muted mt-1">
                    Каналът по подразбиране се отваря автоматично при влизане. Само един канал може да бъде по подразбиране.
                  </div>
                </div>
                {!channel.is_default && (
                  <button
                    onClick={() => setDefaultChannelMutation.mutate(channel.id)}
                    className="btn-primary ml-4 text-sm"
                    disabled={setDefaultChannelMutation.isPending}
                  >
                    {setDefaultChannelMutation.isPending ? 'Задаване...' : 'Задай'}
                  </button>
                )}
                {channel.is_default && (
                  <span className="ml-4 text-sm font-medium text-blue-600 dark:text-blue-400">
                    Активен
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Danger Zone */}
          <div className="border-t border-gray-200 dark:border-dark-border-primary pt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
              Опасна зона
            </h3>

            <div className="space-y-3">
              {/* Archive/Unarchive Button */}
              <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                    {channel.is_archived ? 'Възстанови канала' : 'Архивирай канала'}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {channel.is_archived
                      ? 'Канал ще стане отново активен и видим'
                      : 'Канал ще бъде скрит, но съобщенията ще бъдат запазени'}
                  </div>
                </div>
                <button
                  onClick={handleArchive}
                  className="btn-secondary text-yellow-700 hover:bg-yellow-100 ml-4"
                  disabled={archiveChannelMutation.isPending || unarchiveChannelMutation.isPending}
                >
                  {archiveChannelMutation.isPending || unarchiveChannelMutation.isPending
                    ? 'Обработка...'
                    : channel.is_archived
                    ? 'Възстанови'
                    : 'Архивирай'}
                </button>
              </div>

              {/* Delete Button */}
              {!showDeleteConfirm ? (
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                      Изтрий канала
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Това действие е необратимо. Всички съобщения ще бъдат изтрити.
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="btn-secondary text-red-700 hover:bg-red-100 ml-4"
                  >
                    Изтрий
                  </button>
                </div>
              ) : (
                <div className="p-4 bg-red-100 border-2 border-red-300 rounded-lg">
                  <p className="text-sm font-semibold text-red-900 mb-2">
                    Сигурни ли сте?
                  </p>
                  <p className="text-xs text-red-800 mb-3">
                    Това действие е необратимо. Всички съобщения, файлове и настройки
                    на канала ще бъдат изтрити завинаги.
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleDelete}
                      className="btn-primary bg-red-600 hover:bg-red-700 text-white"
                      disabled={deleteChannelMutation.isPending}
                    >
                      {deleteChannelMutation.isPending ? 'Изтриване...' : 'Да, изтрий канала'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="btn-secondary"
                    >
                      Отказ
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-dark-border-primary bg-gray-50 dark:bg-dark-bg-secondary">
          <button onClick={onClose} className="btn-secondary">
            Затвори
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChannelSettingsModal;
